import { CheckCircle, Clock, Ticket, Inbox, Smile, User } from 'lucide-react';

export interface MyPerformanceData {
  ticketsResolved: number;
  openTickets?: number;
  avgResponseTime: number; // minutes
  slaCompliance: number; // percentage 0-100
  customerSatisfaction?: number; // percentage 0-100
}

interface MyPerformanceWidgetProps {
  title?: string;
  data?: MyPerformanceData;
  loading?: boolean;
  className?: string;
}

// Format a minute value as "45m" or "1.2h".
function fmtMinutes(m: number): string {
  if (!m || m <= 0) return '—';
  if (m < 60) return `${Math.round(m)}m`;
  return `${(m / 60).toFixed(1)}h`;
}

function fmtPct(v?: number): string {
  if (v == null) return '—';
  return `${Math.round(v * 10) / 10}%`;
}

export function MyPerformanceWidget({ title, data, loading = false, className = '' }: MyPerformanceWidgetProps) {
  const shell = `border border-[var(--co-line)] bg-[var(--co-raised)] rounded-[10px] shadow-[var(--co-shadow-card)] h-full flex flex-col overflow-hidden ${className}`;

  if (loading) {
    return (
      <div className={shell}>
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/2 animate-pulse" />
        </div>
        <div className="flex-1 grid grid-cols-2 gap-3 p-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-16 bg-gray-100 dark:bg-gray-700 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={shell}>
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title || 'My Performance'}</h3>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center text-center p-4">
          <User className="h-8 w-8 text-gray-300 dark:text-gray-600 mb-2" />
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No performance data yet</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Your stats appear once you have tickets</p>
        </div>
      </div>
    );
  }

  const stats = [
    { label: 'Resolved', value: String(data.ticketsResolved ?? 0), icon: CheckCircle, color: 'text-green-500' },
    { label: 'Open', value: String(data.openTickets ?? 0), icon: Inbox, color: 'text-blue-500' },
    { label: 'Avg Response', value: fmtMinutes(data.avgResponseTime), icon: Clock, color: 'text-amber-500' },
    { label: 'SLA', value: fmtPct(data.slaCompliance), icon: Ticket, color: 'text-primary-500' },
    { label: 'CSAT', value: fmtPct(data.customerSatisfaction), icon: Smile, color: 'text-pink-500' },
  ];

  return (
    <div className={shell}>
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title || 'My Performance'}</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Your personal overview</p>
      </div>
      <div className="flex-1 min-h-0 p-3 grid grid-cols-2 gap-2 sm:grid-cols-3 content-start">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="rounded-lg bg-gray-50 dark:bg-gray-700/40 p-3 flex flex-col gap-1">
              <div className="flex items-center gap-1.5">
                <Icon className={`w-3.5 h-3.5 ${s.color}`} />
                <span className="text-[11px] text-gray-500 dark:text-gray-400">{s.label}</span>
              </div>
              <span className="text-lg font-bold text-gray-900 dark:text-white">{s.value}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
